// /login — admin 双轨登录页：
//   1. CF Access（super admin / 现有内部账号）：跳 / 让 CF 边缘 IdP 接管
//   2. 邮箱+密码 / 邮箱+验证码：调 /auth/admin-login，仅 role='admin' 用户允许
//
// dev (localhost) 没 CF Access，但有 DEV_ADMIN_BYPASS=1，路径 1 仍然是
// "返回 / 进站" 的快捷入口。
//
// Mounted OUTSIDE Layout — 出现于无 admin 会话时（cookie 过期 / 主动退出）。

import { useEffect, useState, type FormEvent } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { LogIn, Mail, KeyRound, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useToast } from '@/components/ui/Toast'
import { FuelioIcon } from '@/components/FuelioIcon'
import { useI18n } from '@/lib/i18n'
import { markSignedIn } from '@/lib/session'
import { adminLogin, sendAdminLoginCode } from '@/lib/api'

type Tab = 'password' | 'code'

export function LoginScreen() {
  const { t, lang } = useI18n()
  const isZh = lang === 'zh'
  const toast = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const justSignedOut = params.get('signed-out') === '1'

  const [tab, setTab] = useState<Tab>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [resendIn, setResendIn] = useState(0)

  useEffect(() => {
    if (resendIn <= 0) return
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [resendIn])

  // CF Access path: 单击 → 跳 /cf-login。生产环境 CF Access 应用只盯
  // 这一个路径，触发 SSO challenge → 写 CF_Authorization cookie → 服务
  // 端 functions/cf-login.ts 302 回 /。如果 SPA 全站都被 CF Access 网关挡，
  // 普通管理员就连 /login 都看不到 —— 这条把 Access 范围收窄成只盯一个
  // 引导端点。dev 没有 CF 这层（DEV_ADMIN_BYPASS=1），跳 /cf-login 不存在
  // 会 404，所以 dev 仍然直接跳 /，由 dev-bypass 接住。
  function signInViaAccess() {
    markSignedIn()
    const isDev = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
    window.location.href = isDev ? '/' : '/cf-login'
  }

  async function handleSendCode() {
    if (!email) { setErr(isZh ? '请先输入邮箱' : 'Enter email first'); return }
    setBusy(true); setErr('')
    try {
      await sendAdminLoginCode(email)
      setCodeSent(true)
      setResendIn(60)
      toast.success(isZh ? '验证码已发送' : 'Code sent')
    } catch (e: any) {
      // ApiError 的字段是 payload 不是 data —— 这里是为了和后端的 { error }
      // 字段对齐，下同。
      const code = (e?.payload as any)?.error || e?.message || ''
      if (code === 'too_soon') setErr(isZh ? '请稍后再试' : 'Try again shortly')
      else setErr(isZh ? '发送失败，请重试' : 'Failed to send code')
    } finally {
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    if (!email) { setErr(isZh ? '请先输入邮箱' : 'Enter email'); return }
    if (tab === 'password' && !password) { setErr(isZh ? '请输入密码' : 'Enter password'); return }
    if (tab === 'code' && code.length !== 6) { setErr(isZh ? '验证码 6 位' : 'Code must be 6 digits'); return }

    setBusy(true)
    try {
      await adminLogin(tab === 'password' ? { email, password } : { email, code })
      markSignedIn()
      // adminLogin 已经设了 cookie，直接跳 / —— Layout 调 /admin/whoami 拿到身份
      navigate('/')
    } catch (e: any) {
      const errCode = (e?.payload as any)?.error || e?.message || ''
      if (errCode === 'invalid_credentials') setErr(isZh ? '邮箱或密码不正确' : 'Wrong email or password')
      else if (errCode === 'invalid_or_expired_code') setErr(isZh ? '验证码错误或已过期' : 'Code wrong or expired')
      else if (errCode === 'forbidden_not_admin') setErr(isZh ? '此账号不是管理员，无法登录后台' : 'This account is not an admin')
      else if (errCode === 'too_many_attempts') setErr(isZh ? '尝试次数过多，稍后再试' : 'Too many attempts')
      else setErr(isZh ? `登录失败，请重试（${errCode || '未知错误'}）` : `Login failed (${errCode || 'unknown'})`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-7 shadow-sm space-y-5">
        <div className="text-center space-y-2">
          {/* 正版 Fuelio 图标自带紫色 squircle 背景 + 白 F + 右上小球，
              不需要外层再包一个色块容器。size 48 跟原 w-12 视觉占位一致。 */}
          <div className="mx-auto w-12 h-12 flex items-center justify-center">
            <FuelioIcon size={48}/>
          </div>
          <h1 className="text-lg font-semibold tracking-tight">{isZh ? 'Fuelio 管理后台' : 'Fuelio admin'}</h1>
          <p className="text-xs text-muted-foreground">{isZh ? '请使用管理员账号登录' : 'Sign in with an admin account'}</p>
        </div>

        {/* "已退出"通知条 —— 之前样式 (border + bg-success/10 + 等宽) 容易被
            误认成另一个聚焦输入框。改成左对齐 + 图标 + 仅左边 accent 条，
            视觉上明确是 notification 而不是 input。 */}
        {justSignedOut && (
          <div className="flex items-center gap-2 rounded-md bg-success/10 border-l-2 border-success px-3 py-2 text-xs text-success/90">
            <CheckCircle2 size={14} strokeWidth={2}/>
            <span>{isZh ? '你已退出登录' : 'Signed out successfully'}</span>
          </div>
        )}

        {/* Tab switch */}
        <div className="flex gap-1 rounded-md bg-accent/30 p-1 text-xs">
          <button
            type="button"
            onClick={() => { setTab('password'); setErr('') }}
            className={'flex-1 rounded px-3 py-1.5 transition-colors ' + (tab === 'password' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >
            {isZh ? '密码登录' : 'Password'}
          </button>
          <button
            type="button"
            onClick={() => { setTab('code'); setErr('') }}
            className={'flex-1 rounded px-3 py-1.5 transition-colors ' + (tab === 'code' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >
            {isZh ? '验证码登录' : 'Email code'}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" noValidate>
          <label className="block">
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Mail size={11}/>{isZh ? '邮箱' : 'Email'}</span>
            <Input
              type="email"
              autoComplete="email"
              placeholder="admin@fuelio.net"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
            />
          </label>

          {tab === 'password' ? (
            <label className="block">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5"><KeyRound size={11}/>{isZh ? '密码' : 'Password'}</span>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </label>
          ) : (
            <label className="block">
              <span className="text-xs text-muted-foreground">{isZh ? '验证码' : 'Verification code'}</span>
              <div className="mt-1 flex gap-2">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="font-mono tracking-widest text-center"
                />
                {/* whitespace-nowrap：中文字符之间默认是断行机会，"发送" 在窄
                    flex 容器里会被竖排成"发\n送"。shrink-0 防止 Input 抢空间
                    把按钮挤窄。min-w 锁宽度，倒计时态从 "60s" 切回 "发送"
                    不会跳动。 */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={busy || !email || resendIn > 0}
                  className="whitespace-nowrap shrink-0 min-w-[72px]"
                >
                  {resendIn > 0 ? `${resendIn}s` : (isZh ? '发送' : 'Send')}
                </Button>
              </div>
              {codeSent && resendIn > 0 && (
                <div className="mt-1 text-[11px] text-success">
                  {isZh ? '验证码已发送，10 分钟内有效' : 'Code sent — valid for 10 min'}
                </div>
              )}
            </label>
          )}

          {err && <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">{err}</div>}

          <Button type="submit" disabled={busy} className="w-full justify-center">
            {busy ? '...' : (isZh ? '登录' : 'Sign in')}
          </Button>
        </form>

        {/* CF Access 通道 — Super admin / dev bypass */}
        <div className="border-t border-border pt-4 text-center space-y-2">
          <div className="text-[11px] text-muted-foreground">{isZh ? '或' : 'OR'}</div>
          <Button variant="outline" onClick={signInViaAccess} className="w-full justify-center">
            <LogIn size={14} strokeWidth={1.75}/>
            {isZh ? 'Cloudflare Access 登录' : 'Cloudflare Access'}
          </Button>
          <div className="text-[11px] text-muted-foreground/70">
            {isZh ? '内部 / 超级管理员通道' : 'Internal / super-admin channel'}
          </div>
        </div>
      </div>
    </div>
  )
}

